// App constants
export const AUTH_URL = '';
export const USER_TOKEN_KEY = 'USER_TOKEN_KEY';
export const USER_OBJECT_KEY = 'USER_OBJECT_KEY';
export const IS_TOKEN_EXPIRED = 'IS_TOKEN_EXPIRED';
export const IS_ADMIN_PANEL_ON_TOP = 'IS_ADMIN_PANEL_ON_TOP';
export const IMPORT_SESSION_FILE_DATA = 'IMPORT_SESSION_FILE_DATA';

// Environment constants
export const BASE_URL = process.env.REACT_APP_BASE_URL ?? '';
export const REACT_APP_AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN ?? '';
export const REACT_APP_AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID ?? '';
export const REACT_APP_API_CONNECTION_NAME = process.env.REACT_APP_API_CONNECTION_NAME ?? '';
export const REACT_APP_API_PASSWORD = process.env.REACT_APP_API_PASSWORD ?? '';
export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
export const REACT_APP_AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE ?? '';

// Amazon Connect — instance URL (no trailing slash) and AWS region.
// Defaults match the production Amtek instance so the softphone works without
// extra env config in dev; override per-env if pointing at a sandbox instance.
export const REACT_APP_CONNECT_INSTANCE_URL =
  process.env.REACT_APP_CONNECT_INSTANCE_URL ?? 'https://amtek.my.connect.aws';
export const REACT_APP_CONNECT_REGION = process.env.REACT_APP_CONNECT_REGION ?? 'ap-southeast-2';
// Connect instance UUID — needed for backend-side AWS SDK calls (recording
// pause/resume, contact-attribute updates). Found in the AC console under
// Account overview → Instance ID.
export const REACT_APP_CONNECT_INSTANCE_ID =
  process.env.REACT_APP_CONNECT_INSTANCE_ID ?? 'dd4fa8c7-08b6-4a0e-b875-6db09a2a82ac';
// Lambda URL that brokers everything the browser can't call directly:
// recording pause/resume, disposition tree fetch, disposition submit. Single
// endpoint, dispatched on `feature` + `action` fields in the body.
export const REACT_APP_CONNECT_API_URL =
  process.env.REACT_APP_CONNECT_API_URL ??
  'https://uryih7fr2jcgxnq2kakas3goz40itmif.lambda-url.ap-southeast-2.on.aws/';
// Disposition tree key prefix — the lambda stores trees under
// `<prefix>.<channel>` (e.g. `amtek.voice`).
export const REACT_APP_CONNECT_CONFIG_NAME = process.env.REACT_APP_CONNECT_CONFIG_NAME ?? 'amtek';
export const REACT_APP_STRUCTURE_IMPORT_EXCEL_KEY =
  process.env.REACT_APP_STRUCTURE_IMPORT_EXCEL_KEY ?? '';
export const REACT_APP_SERVICE_IMPORT_EXCEL_KEY =
  process.env.REACT_APP_SERVICE_IMPORT_EXCEL_KEY ?? '';
export const DEFAULT_PRINTER_NAME = process.env.REACT_APP_DEFAULT_PRINTER_NAME || '';
export const REACT_APP_QZ_CERTIFICATE =
  process.env.REACT_APP_QZ_CERTIFICATE?.replace(/\\n/gm, '\n') || '';

// File upload mime types.
// Defaults below run when the matching REACT_APP_* var is unset so uploads keep
// working without per-environment config; the empty-string fallback that used
// to be here silently rejected every file as "Wrong file type".
// SVG is excluded from image defaults — uploaded SVGs can carry inline scripts.
const DEFAULT_IMAGE_MIME_TYPES = 'image/jpeg,image/png,image/gif,image/webp';
const DEFAULT_ATTACHMENT_MIME_TYPES = `${DEFAULT_IMAGE_MIME_TYPES},application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;
const DEFAULT_IMPORT_MIME_TYPES =
  'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Trims whitespace and drops empty tokens so downstream `.split(',').includes()`
// exact-matching still works when a deploy env sets a value like
// "image/jpeg, image/png" (the leading-space token would otherwise never match).
// Falls back to the default when the env normalises to empty (unset, "", or
// pure whitespace/commas) — an empty allowlist would silently reject every file.
const normalizeMimeEnv = (value: string | undefined, fallback: string): string => {
  const normalized = (value ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
    .join(',');
  return normalized || fallback;
};

export const IMPORT_FILE_MIME_TYPES = normalizeMimeEnv(
  process.env.REACT_APP_IMPORT_FILE_MIME_TYPES,
  DEFAULT_IMPORT_MIME_TYPES
);
export const USER_PROFILE_MIME_TYPES = normalizeMimeEnv(
  process.env.REACT_APP_USER_PROFILE_MIME_TYPES,
  DEFAULT_IMAGE_MIME_TYPES
);
export const CLIENT_CONTRACT_LOGO_MIME_TYPES = normalizeMimeEnv(
  process.env.REACT_APP_CLIENT_CONTRACT_LOGO_MIME_TYPES,
  DEFAULT_IMAGE_MIME_TYPES
);
export const PART_TYPES_IMAGES_MIME_TYPES = normalizeMimeEnv(
  process.env.REACT_APP_PART_TYPES_IMAGES_MIME_TYPES,
  DEFAULT_IMAGE_MIME_TYPES
);
export const PART_IMAGES_MIME_TYPES = normalizeMimeEnv(
  process.env.REACT_APP_PART_IMAGES_MIME_TYPES,
  DEFAULT_IMAGE_MIME_TYPES
);
export const PART_TYPES_ATTACHMENTS_MIME_TYPES = normalizeMimeEnv(
  process.env.REACT_APP_PART_TYPES_ATTACHMENTS_MIME_TYPES,
  DEFAULT_ATTACHMENT_MIME_TYPES
);
export const SERVICE_ATTACHMENTS_MIME_TYPES = normalizeMimeEnv(
  process.env.REACT_APP_SERVICE_ATTACHMENTS_MIME_TYPES,
  DEFAULT_ATTACHMENT_MIME_TYPES
);
export const ADD_STOCK_ATTACHMENTS_MIME_TYPES = normalizeMimeEnv(
  process.env.REACT_APP_ADD_STOCK_ATTACHMENTS_MIME_TYPES,
  DEFAULT_ATTACHMENT_MIME_TYPES
);

// CVTS node IDs
export const REACT_APP_CVTS_CLIENT_NODE_ID = process.env.REACT_APP_CVTS_CLIENT_NODE_ID || '';
export const REACT_APP_CVTS_CONTRACT_NODE_ID = process.env.REACT_APP_CVTS_CONTRACT_NODE_ID || '';
